import React from 'react';
import { ApolloError } from '@apollo/client';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import AppDialog from '../../../../../shared/display/dialog/Dialog';
import { NotificationType } from '../../../../../shared/display/form/notifications';
import { AppAddress } from '../../../../../shared/display/form/PlacesAutocomplete';
import AppInput from '../../../../../shared/display/form/Input';
import AppPhoneNumberInput from '../../../../../shared/display/phone/PhoneNumberInput';
import { AppPhoneNumberProps } from '../../../../../shared/display/phone/PhoneNumber';
import { AppValue } from '../../../../../shared/data/types';
import CreateUserOptions from './CreateUserOptions';
import ProfileWorkAddressInput from '../../../../account/settings/personalInfo/cards/contactInfo/workAddress/ProfileWorkAddressInput';
import AppSelect from '../../../../../shared/display/select/Select';
import { memberTypes } from '../../../../../shared/data/constants';

export type UserForm = {
  firstName: string,
  lastName: string,
  position: string,
  company: string,
  region: string,
  mobilePhone: AppPhoneNumberProps | null,
  officePhone: AppPhoneNumberProps | null,
  location?: AppAddress,
  workspace?: AppValue | null,
  workspaceRole?: AppValue | null,
  email?: string,
  memberType?: AppValue | null,
  changes: string[],
}

function isFormValid(user: UserForm, isCreate?: boolean): boolean {
  return user.firstName !== '' && user.lastName !== '' && (!isCreate || (user.workspace !== undefined && user.workspace != null && user.email !== '' && user.email !== undefined));
}

type UserDetailsDialogProps = {
  isAdmin: boolean,
  title: string,
  user: UserForm,
  open: boolean,
  loading: boolean,
  error?: ApolloError,
  isCreate?: boolean,
  onClose: () => void,
  onConfirm: (data: UserForm) => void,
};

function UserDetailsDialog({ isAdmin, title, user, open, loading, error, isCreate, onClose, onConfirm }: UserDetailsDialogProps) {
  const [formData, setFormData] = React.useState<UserForm>(user);
  const [notificationType, setNotificationType] = React.useState<NotificationType>('None');

  React.useEffect(
    () => {
      setNotificationType(loading ? 'Loader' : error ? 'Error' : 'None');
    },
    [loading, error]
  );

  React.useEffect(
    () => {
      setFormData(user);
    },
    [user]
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      onConfirm={() => onConfirm(formData)}
      onGoBack={() => setNotificationType('None')}
      title={title}
      confirmEnabled={!loading && isFormValid(formData, isCreate)}
      notificationType={notificationType}
      notification={error?.message}
      disableContentPadding
      minWidth={300}
    >
      <List>
        {isCreate && (
          <CreateUserOptions
            formData={formData}
            setFormData={setFormData}
          />
        )}
        <ListItem>
          <AppInput
            name="firstName"
            label="First Name"
            value={formData.firstName}
            onChange={(value) => setFormData({ ...formData, firstName: value, changes: [...formData.changes, 'firstName'] })}
          />
        </ListItem>
        <ListItem>
          <AppInput
            name="lastName"
            label="Last Name"
            value={formData.lastName}
            onChange={(value) => setFormData({ ...formData, lastName: value, changes: [...formData.changes, 'lastName'] })}
          />
        </ListItem>
        <ListItem>
          <AppInput
            name="position"
            label="Position"
            value={formData.position}
            onChange={(value) => setFormData({ ...formData, position: value, changes: [...formData.changes, 'position'] })}
          />
        </ListItem>
        {!isCreate && isAdmin &&
          <ListItem>
            <AppSelect
              name="type"
              options={memberTypes}
              value={formData.memberType || null}
              onChange={(value) => setFormData({ ...formData, memberType: value, changes: [...formData.changes, 'memberType'] })}
              clearable
            />
          </ListItem>
        }
        <ListItem>
          <AppInput
            name="company"
            label="Company"
            value={formData.company}
            onChange={(value) => setFormData({ ...formData, company: value, changes: [...formData.changes, 'company'] })}
          />
        </ListItem>
        <ListItem>
          <AppInput
            name="region"
            label="Region"
            value={formData.region}
            onChange={(value) => setFormData({ ...formData, region: value, changes: [...formData.changes, 'region'] })}
          />
        </ListItem>
        <ListItem>
          <AppPhoneNumberInput
            name="Mobile Phone"
            value={formData.mobilePhone}
            onChange={(value) => setFormData({ ...formData, mobilePhone: value, changes: [...formData.changes, 'mobilePhone'] })}
          />
        </ListItem>
        <ListItem>
          <AppPhoneNumberInput
            name="Office Phone"
            value={formData.officePhone}
            onChange={(value) => setFormData({ ...formData, officePhone: value, changes: [...formData.changes, 'officePhone'] })}
            hasExtension
          />
        </ListItem>
        <ProfileWorkAddressInput
          address={formData.location || { rawAddress: '' }}
          onChange={(value) => setFormData({ ...formData, location: value, changes: [...formData.changes, 'location'] })}
        />
      </List>
    </AppDialog>
  );
}

export default UserDetailsDialog;
